/**
 * FAQ copy split by audience: public (/faq when logged out) vs in-app Help.
 * EN only — keep answers short with one clear next step each.
 */

/** Tier flags mirrored from useTierAccess / useAppNavItems */
export type FaqAccessRequirement =
  | "hasAIAssistant"
  | "hasFounderReport"
  | "hasAlignmentAdvantage"
  | "hasMonthlyConsultation";

export interface FaqItem {
  id: string;
  question: string;
  /** Short intro before steps, or full answer when `steps` is omitted */
  answer: string;
  /** Numbered steps shown as an ordered list when present */
  steps?: string[];
  /** Optional primary action shown under the answer */
  action?: {
    label: string;
    href: string;
    external?: boolean;
  };
  /** When set, item is shown only if the user has this program access */
  requires?: FaqAccessRequirement;
}

export const FAQ_SUPPORT_EMAIL = "askcae@topasiaedu.com";
export const FAQ_APP_URL = "https://app.caegoh.com/";
export const FAQ_ACADEMY_URL = "https://academy.caegoh.com";

/** Pre-login FAQ (sign-in page, public /faq) */
export const FAQ_PUBLIC_ITEMS: FaqItem[] = [
  {
    id: "create-account",
    question: "How do I create an account?",
    answer: "You create your own account and choose your own password.",
    steps: [
      `Go to ${FAQ_ACADEMY_URL} and sign in to the academy portal.`,
      "Open Design Your Destiny Pre-Training → Step by Step Guidance To Access Software.",
      "Watch How to Create an Account and copy the referral code shown in that module.",
      `Open ${FAQ_APP_URL}, start sign-up, enter the referral code, and set your password.`,
    ],
    action: {
      label: "Open the academy",
      href: FAQ_ACADEMY_URL,
      external: true,
    },
  },
  {
    id: "sign-in",
    question: "How do I sign in?",
    answer: "Sign-in uses email and password only, not SMS or one-time codes.",
    steps: [
      `Go to ${FAQ_APP_URL}`,
      "Enter the email you used when you created your account.",
      "Enter the password you set at sign-up, then sign in.",
    ],
    action: {
      label: "Go to Sign In",
      href: "/authentication/sign-in",
    },
  },
  {
    id: "invalid-credentials",
    question: "I see “Invalid email or password”",
    answer: "This usually means the wrong email, a typo, or an outdated password.",
    steps: [
      "Confirm you’re using the same email address you registered with.",
      "Check for typos in your email or password (passwords are case-sensitive).",
      "Try again carefully.",
      "If you’re still blocked, reset your password and sign in with the new one.",
    ],
    action: {
      label: "Reset your password",
      href: "/authentication/forgot-password",
    },
  },
  {
    id: "forgot-password",
    question: "I forgot my password",
    answer: "We email a reset link, not a temporary password.",
    steps: [
      "Open the sign-in page.",
      "Tap Forgot password?",
      "Enter your registered email and submit the form.",
      "Open the reset link in your email and set a new password.",
      "Return to sign-in and use your new password.",
    ],
    action: {
      label: "Forgot password",
      href: "/authentication/forgot-password",
    },
  },
  {
    id: "no-reset-email",
    question: "I didn’t receive the password reset email",
    answer: "Reset emails can take a few minutes or land in another folder.",
    steps: [
      "Check Spam, Junk, and Promotions (or similar folders).",
      "Wait a few minutes, then check again.",
      "Confirm you entered the same email you used to register.",
      "Request one more reset from Forgot password (avoid repeated rapid requests).",
      `If nothing arrives, email ${FAQ_SUPPORT_EMAIL} and tell us which login email you used.`,
    ],
    action: {
      label: `Email ${FAQ_SUPPORT_EMAIL}`,
      href: `mailto:${FAQ_SUPPORT_EMAIL}?subject=${encodeURIComponent(
        "Password reset email not received"
      )}`,
      external: true,
    },
  },
  {
    id: "reset-link-expired",
    question: "My reset link expired or doesn’t work",
    answer: "Reset links expire for security.",
    steps: [
      "Go back to the sign-in page and tap Forgot password? again.",
      "Request a fresh reset link.",
      "Open the new link promptly, ideally on the same device and browser.",
      "Set your new password right away.",
    ],
    action: {
      label: "Request a new reset link",
      href: "/authentication/forgot-password",
    },
  },
  {
    id: "still-stuck",
    question: "I’m still stuck. Who do I contact?",
    answer: "Support can help when the steps above didn’t resolve it.",
    steps: [
      `Email ${FAQ_SUPPORT_EMAIL} (no CC needed).`,
      "Include your full name and login email.",
      "Say what you already tried (for example password reset, which page you’re on).",
      "Attach a screenshot if it helps explain the issue.",
    ],
    action: {
      label: `Email ${FAQ_SUPPORT_EMAIL}`,
      href: `mailto:${FAQ_SUPPORT_EMAIL}?subject=${encodeURIComponent(
        "App help request"
      )}`,
      external: true,
    },
  },
];

/** Logged-in FAQ (Command Centre /faq) — feature how-tos */
export const FAQ_IN_APP_ITEMS: FaqItem[] = [
  {
    id: "calculate",
    question: "How do I calculate a chart for someone else?",
    answer: "Use Calculate to add or reopen non-self profiles.",
    steps: [
      "Open Calculate from the navigation.",
      "Add a new person or pick an existing saved profile from the list.",
      "Complete birth details and run the calculation, then open that person’s chart from the list.",
      "The number badge on Calculate shows how many profiles you have saved.",
    ],
    action: {
      label: "Open Calculate",
      href: "/calculate",
    },
  },
  {
    id: "ai-wealth-assistant",
    requires: "hasAIAssistant",
    question: "How do I use AI Wealth Assistant?",
    answer: "Available when AI Wealth Assistant appears in your menu.",
    steps: [
      "Confirm AI Wealth Assistant is listed in the left navigation.",
      "Open it and wait for the chat view to load.",
      "Ask clear questions about wealth themes; stay on topic for best results.",
      "If the menu item is missing, your program may not include this feature yet.",
    ],
    action: {
      label: "Open AI Wealth Assistant",
      href: "/destiny-wealth-navigator",
    },
  },
  {
    id: "founder-report",
    requires: "hasFounderReport",
    question: "How do I open Founder Report?",
    answer: "Founder Report is a dedicated report when your access includes it.",
    steps: [
      "Check the sidebar for Founder Report.",
      "Open it and use the section list (desktop sidebar or mobile picker) to jump between chapters.",
      "Bookmark the page in your browser if you return to it often.",
    ],
    action: {
      label: "Open Founder Report",
      href: "/founder-report",
    },
  },
  {
    id: "alignment-advantage",
    requires: "hasAlignmentAdvantage",
    question: "How do I open Alignment Advantage and download the PDF?",
    answer: "Alignment Advantage is the in-app strategic playbook.",
    steps: [
      "Open Alignment Advantage from the navigation when you have access.",
      "Browse chapters from the contents list on the left (or the section picker on mobile).",
      "On the main Alignment Advantage page, use Download for the playbook PDF (requires PDF service configuration in your environment).",
      "Use Print preview if you want to review in the browser before saving.",
    ],
    action: {
      label: "Open Alignment Advantage",
      href: "/alignment-advantage",
    },
  },
  {
    id: "monthly-consultation",
    requires: "hasMonthlyConsultation",
    question: "How do I open Monthly Consultation?",
    answer: "Shown in the menu only for accounts with monthly consultation access.",
    steps: [
      "Look for Monthly Consultation in the left navigation.",
      "Open it and pick the month or chapter you need.",
      "Use print or PDF actions on that report if your program provides them on the page.",
    ],
    action: {
      label: "Open Monthly Consultation",
      href: "/monthly-consultation",
    },
  },
  {
    id: "change-password",
    question: "How do I change my password in Settings?",
    answer: "Change password while signed in from Settings, not the public reset flow.",
    steps: [
      "Open Settings from the navigation.",
      "Find the password section.",
      "Enter a new password and confirm it (at least 6 characters).",
      "Save, then use the new password the next time you sign in elsewhere.",
    ],
    action: {
      label: "Open Settings",
      href: "/settings",
    },
  },
  {
    id: "missing-feature",
    question: "I don’t see a feature or report in my menu",
    answer: "Menu items depend on your program tier and account status.",
    steps: [
      "Refresh once if access was just granted.",
      "Sign out and sign back in to reload your permissions.",
      "Compare with FAQ topics here: if a report is documented but not in your menu, you may not have that program yet.",
      `Email ${FAQ_SUPPORT_EMAIL} with your login email and the exact feature name you expected.`,
    ],
    action: {
      label: `Email ${FAQ_SUPPORT_EMAIL}`,
      href: `mailto:${FAQ_SUPPORT_EMAIL}?subject=${encodeURIComponent(
        "Missing app feature"
      )}`,
      external: true,
    },
  },
  {
    id: "access-blocked",
    question: "My membership is paused, expired, or blocked",
    answer: "Membership changes are handled by the CAE team.",
    steps: [
      `Email ${FAQ_SUPPORT_EMAIL}.`,
      "Include your full name and login email.",
      "Describe what you see (paused, expired, or blank access).",
    ],
    action: {
      label: `Email ${FAQ_SUPPORT_EMAIL}`,
      href: `mailto:${FAQ_SUPPORT_EMAIL}?subject=${encodeURIComponent(
        "App access / membership help"
      )}`,
      external: true,
    },
  },
  {
    id: "still-stuck",
    question: "I’m still stuck. Who do I contact?",
    answer: "Support can help when the steps above did not resolve it.",
    steps: [
      `Email ${FAQ_SUPPORT_EMAIL} (no CC needed).`,
      "Include your full name and login email.",
      "Say what you tried and which page you are on (include a screenshot if helpful).",
    ],
    action: {
      label: `Email ${FAQ_SUPPORT_EMAIL}`,
      href: `mailto:${FAQ_SUPPORT_EMAIL}?subject=${encodeURIComponent(
        "App help request"
      )}`,
      external: true,
    },
  },
];
